/**
 * Tab Tipi di Visita nell'Admin Panel.
 * CRUD tipi visita con durata.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentsApi, VisitType, VisitTypeCreate } from '../../api/appointments'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function VisitTypesTab() {
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<VisitTypeCreate>({ key: '', label: '', duration_minutes: 30 })

  const queryClient = useQueryClient()

  const { data: visitTypes } = useQuery<VisitType[]>({
    queryKey: ['visit-types'],
    queryFn: () => appointmentsApi.visitTypes.list(),
  })

  const createMutation = useMutation({
    mutationFn: appointmentsApi.visitTypes.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-types'] })
      setShowForm(false)
      setForm({ key: '', label: '', duration_minutes: 30 })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: { duration_minutes: number } }) =>
      appointmentsApi.visitTypes.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-types'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: appointmentsApi.visitTypes.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-types'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la disattivazione')
    },
  })

  const activateMutation = useMutation({
    mutationFn: appointmentsApi.visitTypes.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-types'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la riattivazione')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(form)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Tipi di Visita</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          + Aggiungi Tipo
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-md font-semibold text-foreground mb-4">Nuovo Tipo di Visita</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Chiave</Label>
                  <Input
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="visita_nuova"
                    required
                  />
                </div>
                <div>
                  <Label>Etichetta</Label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="Visita nuova"
                    required
                  />
                </div>
                <div>
                  <Label>Durata (min)</Label>
                  <Input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })}
                    min={5}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Salvataggio...' : 'Salva'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Chiave</TableHead>
              <TableHead>Etichetta</TableHead>
              <TableHead>Durata (min)</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visitTypes?.map((vt) => (
              <TableRow key={vt.key}>
                <TableCell className="font-mono">{vt.key}</TableCell>
                <TableCell>{vt.label}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={vt.duration_minutes}
                    onChange={(e) => {
                      updateMutation.mutate({ key: vt.key, data: { duration_minutes: parseInt(e.target.value) } })
                    }}
                    className="w-20"
                    min={5}
                  />
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    vt.is_active
                      ? 'bg-success/10 text-success border-success/20'
                      : 'bg-muted text-muted-foreground border-border'
                  }>
                    {vt.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {vt.is_active ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deactivateMutation.mutate(vt.key)}
                    >
                      Disattiva
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => activateMutation.mutate(vt.key)}
                    >
                      Riattiva
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
